import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCategoryStore } from '@/stores/categoryStore';
import { BlahblahCard } from '@/components/BlahblahCard';
import { cn } from '@/lib/utils';
import { ArrowLeft, Star, Loader2 } from 'lucide-react';
import type { FeedSortType } from '@/types';

// Same icon map as CategoriesPage
const iconMap: Record<string, string> = {
  'select_all': '📋',
  'auto_awesome': '✨',
  'palette': '🎨',
  'hearing': '👂',
  'face_retouching_natural': '💄',
  'menu_book': '📚',
  'work': '💼',
  'directions_car': '🚗',
  'star': '⭐',
  'sentiment_very_satisfied': '😄',
  'content_cut': '✂️',
  'currency_bitcoin': '₿',
  'today': '📅',
  'nightlife': '💃',
  'school': '🎓',
  'theater_comedy': '🎭',
  'emoji_events': '🏆',
  'checkroom': '👔',
  'account_balance': '🏦',
  'fitness_center': '💪',
  'restaurant': '🍽️',
  'smartphone': '📱',
  'sports_esports': '🎮',
  'yard': '🌱',
  'health_and_safety': '🏥',
  'history_edu': '📜',
  'home': '🏠',
  'translate': '🌐',
  'tag_faces': '😎',
  'psychology': '🧠',
  'two_wheeler': '🏍️',
  'trending_up': '📈',
  'movie': '🎬',
  'music_note': '🎵',
  'forest': '🌲',
  'newspaper': '📰',
  'record_voice_over': '🗣️',
  'more_horiz': '⋯',
  'hiking': '🥾',
  'visibility': '👁️',
  'family_restroom': '👨‍👩‍👧',
  'pets': '🐾',
  'photo_camera': '📷',
  'how_to_vote': '🗳️',
  'code': '💻',
  'help': '❓',
  'favorite': '❤️',
  'science': '🔬',
  'self_improvement': '🧘',
  'sports_soccer': '⚽',
  'auto_stories': '📖',
  'devices': '🖥️',
  'flight': '✈️',
  'policy': '🔍',
  'public': '🌍',
  'edit_note': '✍️',
  '18_up_rating': '🔞',
};

const sortOptions: { key: FeedSortType; label: string }[] = [
  { key: 'latest', label: 'Latest' },
  { key: 'popular', label: 'Popular' },
  { key: 'trending', label: 'Trending' },
  { key: 'discussed', label: 'Most Discussed' },
];

export function CategoryFeedPage() {
  const { slug } = useParams<{ slug: string }>();

  const {
    categories,
    feedItems,
    currentSort,
    feedLoading,
    feedLoadingMore,
    feedHasMore,
    feedError,
    loadCategories,
    loadCategoryFeed,
    loadMoreCategoryFeed,
    setFeedSort,
    starCategory,
    unstarCategory,
  } = useCategoryStore();

  // Load categories if not loaded
  useEffect(() => {
    if (categories.length === 0) {
      loadCategories();
    }
  }, [categories.length, loadCategories]);

  // Load feed when slug changes
  useEffect(() => {
    if (slug) {
      loadCategoryFeed(slug, currentSort);
    }
  }, [slug, loadCategoryFeed]);

  const category = categories.find((c) => c.slug === slug);
  const icon = category ? iconMap[category.icon] || '📁' : '📁';

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && feedHasMore && !feedLoadingMore) {
      loadMoreCategoryFeed();
    }
  };

  const handleStarToggle = () => {
    if (!category) return;
    if (category.isStarred) {
      unstarCategory(category.id);
    } else {
      starCategory(category.id);
    }
  };

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto" onScroll={handleScroll}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Link to="/categories" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <span className="text-xl" role="img" aria-label={category?.name || slug}>
            {icon}
          </span>
          <h1 className={cn(
            'text-lg font-bold flex-1',
            category?.isAdultOnly ? 'text-red-500' : 'text-card-foreground'
          )}>
            {category?.name || slug}
          </h1>
          {category && (
            <button
              onClick={handleStarToggle}
              className="p-2"
            >
              <Star
                className={cn(
                  'w-5 h-5 transition-colors',
                  category.isStarred
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              />
            </button>
          )}
        </div>

        {/* Sort options */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
          {sortOptions.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFeedSort(key)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                currentSort === key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {feedError && (
        <div className="p-4 text-center">
          <p className="text-red-500 text-sm mb-2">{feedError}</p>
          <button
            onClick={() => slug && loadCategoryFeed(slug, currentSort)}
            className="text-sm text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {feedLoading && feedItems.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Empty state */}
      {!feedLoading && feedItems.length === 0 && !feedError && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <span className="text-4xl mb-4">🎙️</span>
          <p className="text-muted-foreground text-center">
            No blahblahs in this category yet.
            <br />
            Be the first to share your voice!
          </p>
        </div>
      )}

      {/* Feed items */}
      <div className="divide-y divide-border">
        {feedItems.map((item) => (
          <BlahblahCard key={item.id} blahblah={item} />
        ))}
      </div>

      {/* Loading more */}
      {feedLoadingMore && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* End of feed */}
      {!feedHasMore && feedItems.length > 0 && (
        <div className="py-4 text-center text-sm text-muted-foreground">
          You've reached the end
        </div>
      )}
    </div>
  );
}
